import React from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as IconStopMapCircle } from '../../../assets/svg/stop-map-circle.svg';
import { ReactComponent as IconChevronRight } from '../../../assets/svg/chevron-right.svg';
import { Keyboard } from '../../../utils/Keyboard';
import { SEARCH_MODE } from '../../../services/TimetablesService';
import AliasMacronService from '../../../services/AliasMacronService';

const bem = 'tt-stoplist';

export const getStopLabel = (stop, searchMode) => `Stop ${searchMode !== SEARCH_MODE.TRAIN ? stop.stop_code : stop.parent_stop_code}`;

// eslint-disable-next-line no-param-reassign
const disableFocus = refs => refs.forEach((ref) => { ref.current.tabIndex = '-1'; });
// eslint-disable-next-line no-param-reassign
const enableFocus = refs => refs.forEach((ref) => { ref.current.tabIndex = '0'; });

export default class StopListing extends React.PureComponent {
  constructor(props) {
    super(props);
    this.stopRefs = [];
    this.curRefIndex = 0;
  }

  selectStopOnKeyDown = (ev, ...stopData) => {
    const { onClickStop: loadStop } = this.props;
    let nextIndex;
    switch (ev.keyCode) {
      case Keyboard.ARROW_DOWN:
        if (this.stopRefs) {
          nextIndex = (this.curRefIndex >= this.stopRefs.length - 1) ? 0 : this.curRefIndex + 1;
          this.stopRefs[nextIndex].current.focus();
          ev.preventDefault();
        }
        break;
      case Keyboard.ARROW_UP:
        if (this.stopRefs) {
          nextIndex = (this.curRefIndex <= 0) ? this.stopRefs.length - 1 : this.curRefIndex - 1;
          this.stopRefs[nextIndex].current.focus();
          ev.preventDefault();
        }
        break;
      case Keyboard.ENTER:
        loadStop(...stopData);
        break;
      case Keyboard.TAB:
        // each item in the schedule is focusable so it can be traversed using the keyboard. That means that if a user hits
        // the TAB key, the focus will move to the next item in the list. However we only want to allow to traverse the list
        // by arrows and move out of the list when TAB is pressed. To do this, we temporarily disable focusability of the list when
        // TAB is pressed (and reenable it once the focus is out).
        disableFocus(this.stopRefs);
        break;
      default:
        break;
    }
  };

  loadStop = (index, ...stopData) => {
    const { onClickStop } = this.props;
    this.curRefIndex = index;
    onClickStop(...stopData);
  };

  renderStop = (stop, index) => {
    const { routeShortName, selectedRoute, searchMode } = this.props;
    this.stopRefs.push(React.createRef());

    const liClassNames = [`${bem}__row`];
    const iconClassNames = [`${bem}__row-col-circle`, 'icon-circle'];
    if (stop.selected) {
      liClassNames.push(`${bem}__row--selected`);
      iconClassNames.push(`${bem}__row-col-circle--selected`);
    }

    return (
      <li
        data-testid={`${bem}__stop-${stop.stop_code}`}
        className={liClassNames.join(' ')}
        role="tab"
        ref={this.stopRefs[index]}
        key={stop.key}
        onClick={() => this.loadStop(index, stop.key, routeShortName, stop.stop_code, selectedRoute)}
        onKeyDown={ev => this.selectStopOnKeyDown(ev, stop.key, routeShortName, stop.stop_code, selectedRoute)}
        onFocus={() => { this.curRefIndex = index; }}
        // once the user moves the focus out of the list, we want to make sure the list can be focusable again
        onBlur={() => { enableFocus(this.stopRefs); }}
        tabIndex={0}
        aria-label={`${getStopLabel(stop, searchMode)}. ${stop.stop_name}`}
      >
        <span className={`${bem}__row-col`}>
          <IconStopMapCircle
            className={iconClassNames.join(' ')}
            role="img"
          />
        </span>
        <span className={`${bem}__row-col`}>{getStopLabel(stop, searchMode)}</span>
        <span className={`${bem}__row-col`}>{AliasMacronService.getMacronisedText(searchMode !== SEARCH_MODE.TRAIN ? stop.stop_name : stop.parent_stop_name)}</span>
        <span className={`${bem}__row-col`}>
          <IconChevronRight className={`${bem}__row-col-chevron`} role="img" />
        </span>
      </li>
    );
  };

  render() {
    const { stops, loadingStops } = this.props;
    this.stopRefs = [];

    if (!loadingStops && (!stops || stops.length <= 0)) return <p>There are no stops in this route</p>;
    if (loadingStops) return <div className={`${bem}--loading loader`} />;

    return (
      <ul role="tablist" className={`${bem} ${bem}__stops`} tabIndex={-1} aria-label="List of stops. Use up and down arrows to navigate.">
        {
          stops
            .filter(stop => !(stop.pickup_type === 1 && stop.drop_off_type === 1))
            .map((stop, index) => this.renderStop(stop, index))
        }
      </ul>

    );
  }
}

/* ------------- Prop Types Definitions  ------------- */
StopListing.defaultProps = {
  stops: [],
  loadingStops: false,
  routeShortName: null,
  selectedRoute: null,
  onClickStop: null,
};
StopListing.propTypes = {
  stops: PropTypes.array,
  loadingStops: PropTypes.bool,
  routeShortName: PropTypes.string,
  selectedRoute: PropTypes.object,
  onClickStop: PropTypes.func,
  searchMode: PropTypes.string.isRequired,
};